import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Calendar } from 'lucide-react';
import { maybeTransliterate } from '@/utils/transliterate';
import { stripMarkdown } from '@/utils/stripMarkdown';

interface ArticleCardProps {
  /** Routing prefix — 'article' (novi Strapi Articles), 'news' / 'program' (legacy) */
  type?: 'article' | 'news' | 'program';
  id: string;
  slug?: string;
  title: string;
  excerpt: string;
  subtitle?: string;
  image: string;
  publishedAt?: string;
  /** Featured flag — istakni karticu (rame uz rame sa accent-pink ringom) */
  featured?: boolean;
  /** Tema kartice — `light` (gray-50, bijela sekcija) ili `glass` (transparent, tamna sekcija) */
  variant?: 'light' | 'glass';
}

export default function ArticleCard({
  type = 'article',
  id,
  slug,
  title,
  excerpt,
  subtitle,
  image,
  publishedAt,
  featured,
  variant = 'light',
}: ArticleCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || 'bs';

  const linkPath = `/${type}/${slug || id}`;

  const dateLocale =
    locale === 'sr-Cyrl' ? 'sr-Cyrl-RS' : locale === 'sr' ? 'sr-Latn-RS' : locale;

  const formattedDate = publishedAt
    ? new Date(publishedAt).toLocaleDateString(dateLocale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const displayTitle = maybeTransliterate(title, locale);
  const displaySubtitle = subtitle ? maybeTransliterate(subtitle, locale) : '';
  // Excerpt može sadržavati Markdown (iz Strapi `articles.excerpt` ili `programs.excerpt`).
  // Kartica koristi line-clamp-3 — skidamo Markdown da se ne prikazuje sirov u UI-u.
  const displayExcerpt = maybeTransliterate(stripMarkdown(excerpt), locale);

  let cardCls: string;
  if (variant === 'glass') {
    cardCls =
      'group block rounded-2xl glass overflow-hidden hover:shadow-xl hover:border-accent-pink/40 transition-all duration-300';
  } else if (featured) {
    cardCls =
      'group block rounded-2xl bg-gray-50 border border-accent-pink/40 overflow-hidden hover:border-accent-pink hover:shadow-xl hover:shadow-accent-pink/20 transition-all duration-300';
  } else {
    cardCls =
      'group block rounded-2xl bg-gray-50 border border-gray-100 overflow-hidden hover:border-accent-pink/40 hover:shadow-lg transition-all duration-300';
  }

  const titleCls =
    variant === 'glass'
      ? 'font-semibold text-white mb-2 group-hover:text-accent-pink transition-colors line-clamp-2'
      : 'font-semibold text-gray-900 mb-2 group-hover:text-accent-pink transition-colors line-clamp-2';

  const excerptCls =
    variant === 'glass'
      ? 'text-text-secondary line-clamp-3 mb-4'
      : 'text-gray-600 line-clamp-3 mb-4';

  const subtitleCls =
    variant === 'glass'
      ? 'text-sm text-accent-pink mb-2'
      : 'text-sm text-text-muted mb-2';

  return (
    <Link to={linkPath} className={cardCls} aria-label={displayTitle}>
      <div className="news-page-card-image overflow-hidden" role="img" aria-label={displayTitle}>
        <img
          src={image}
          alt=""
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-6">
        {formattedDate && (
          <div className="flex items-center gap-1 text-xs text-text-muted mb-2 uppercase tracking-wide">
            <Calendar size={12} />
            <span>{formattedDate}</span>
          </div>
        )}
        <h3 className={titleCls}>{displayTitle}</h3>
        {displaySubtitle && <p className={subtitleCls}>{displaySubtitle}</p>}
        <p className={excerptCls}>{displayExcerpt}</p>
        <div className="flex items-center gap-1 text-accent-pink font-medium text-sm group-hover:gap-2 transition-all">
          <span>{t('news.readMore')}</span>
          <ArrowRight size={16} />
        </div>
      </div>
    </Link>
  );
}
